import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { OTPInput, SlotProps } from 'input-otp'
import { useCallback, useState } from 'react'
import { z } from 'zod'
import { LinkButton } from '~/features/navigation'
import { useTRPC } from '~/services/trpc'
import { SetupWizard } from '~/templates/wizard/presets/setup'

import { Alert, cn } from '@hap/components'

export const Route = createFileRoute('/_public/signup/code')({
  validateSearch: z.object({ email: z.string() }),
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC()
  const mutator = useMutation(trpc.signup.verifyCode.mutationOptions())
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(async (code: string) => {
    await mutator
      .mutateAsync({ email: search.email, code })
      .then((response) => {
        navigate({
          to: '/signup/organization',
          search: { token: response.signupToken, email: search.email },
        })
      })
      .catch((mutationError) => {
        setError(mutationError.message || 'An error occurred while verifying the code.')
      })
  }, [])

  return (
    <SetupWizard title="Sign up for Happitu" currentStep={1}>
      <section className="bg-accent grid gap-8 p-20">
        <header className="mx-auto mb-8 max-w-[440px] space-y-4 text-center">
          <h1 className="text-4xl font-bold">We emailed you a signup code</h1>
          <p className="text-xl">
            We sent an email to <strong className="font-semibold">{search.email}</strong>.
            If you don’t see the email, check your spam or junk folder.
          </p>
        </header>

        <div className="grid gap-2">
          <div>
            <OTPInput
              maxLength={6}
              onComplete={handleSubmit}
              render={({ slots }) => (
                <div className="grid grid-cols-6 items-center gap-0">
                  {slots.map((slot, idx) => (
                    <Slot key={idx} {...slot} />
                  ))}
                </div>
              )}
            />
          </div>

          {error && (
            <Alert variant="warn" className="mt-4">
              {error}
            </Alert>
          )}

          <div className="grid">
            <LinkButton
              to="/signup"
              variant="secondary"
              icon="arrow-left"
              iconClassName="size-4"
            >
              Back to sign up options
            </LinkButton>
          </div>
        </div>
      </section>
    </SetupWizard>
  )
}

function Slot(props: SlotProps) {
  return (
    <div
      className={cn(
        'relative h-30 font-mono text-[4rem]',
        'flex items-center justify-center',
        'transition-all duration-300',
        'border-border-dark border-y border-r first:rounded-l-md first:border-l last:rounded-r-md',
        'bg-background',
        'outline-hidden outline-0',
        {
          'bg-background border-input-focus-border z-10 shadow-[0px_0px_0px_3px_hsl(var(--input-focus-shadow))] outline-hidden':
            props.isActive,
        },
      )}
    >
      <div className="group-has-[input[data-input-otp-placeholder-shown]]:opacity-20">
        {props.char ?? props.placeholderChar}
      </div>
      {props.hasFakeCaret && <FakeCaret />}
    </div>
  )
}

function FakeCaret() {
  return (
    <div className="animate-caret-blink pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="h-8 w-px bg-white" />
    </div>
  )
}
